import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Upload, Loader2, CheckCircle2, AlertTriangle, ArrowRight, MapPin, Copy, Wallet,
} from 'lucide-react';
import { MultiLoadStopEditor, stopHasLocation } from './MultiLoadStopEditor';
import { usePayPreview, fmtPay } from '../hooks/usePayPreview';
import { PayStructureNudge, missingPayDrivers } from './PayStructureNudge';
import { DriverMatchCell } from './DriverMatchCell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.csv,application/pdf,image/png,image/jpeg,image/webp,text/csv';

const fmtMoney = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMiles = (v) => `${Number(v || 0).toLocaleString()} mi`;

const mapStop = (s, forceType) => ({
  type: (forceType || s.type || s.stop_type || 'delivery').toLowerCase(),
  facility: s.facility || s.code || '',
  address: s.address || '',
  city: s.city || '',
  state: (s.state || '').toUpperCase(),
  zip: s.zip || s.zip_code || '',
  appointment_date: s.appointment_date || s.planned_arrival_date || s.date || '',
  appointment_time: s.appointment_time || s.planned_arrival_time || s.time || '',
});
const canonStops = (g) => {
  const seq = [];
  if (g.pickup && (g.pickup.facility || g.pickup.code || g.pickup.city)) seq.push(mapStop(g.pickup, 'pickup'));
  (Array.isArray(g.stops) ? g.stops : []).forEach((s) => seq.push(mapStop(s, 'delivery')));
  if (seq.length) return seq;
  if (g.delivery && (g.delivery.facility || g.delivery.code || g.delivery.city)) {
    return [mapStop(g.pickup || {}, 'pickup'), mapStop(g.delivery, 'delivery')];
  }
  return [];
};

const toISODate = (s) => {
  if (!s) return '';
  const str = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) {
    let [, mo, d, y] = m;
    if (y.length === 2) y = '20' + y;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
};

const isCsv = (file) => /\.csv$/i.test(file?.name || '') || file?.type === 'text/csv';

const MultiLoadImportModal = ({ open, onClose, onDone, drivers = [], initialFile = null }) => {
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [validationIssues, setValidationIssues] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setBusy(false); setFileName(''); setPreview(null); setImporting(false);
    setImportResult(null); setOverwrite(false); setValidationIssues([]); setExpanded(null);
  }, []);

  const parseFile = useCallback(async (file) => {
    if (!file) return;
    setBusy(true);
    setPreview(null);
    setImportResult(null);
    setFileName(file.name);
    try {
      const form = new FormData();
      form.append('file', file);
      const url = isCsv(file) ? `${API}/loads/amazon/preview` : `${API}/loads/amazon/preview-image`;
      const res = await axios.post(url, form, { withCredentials: true });
      const groups = (res.data.groups || []).map((g) => {
        const sug = res.data.driver_suggestions?.[g.driver_name];
        const auto = sug?.match_status === 'matched' && sug?.driver_id;
        return {
          ...g,
          driver_id: auto ? sug.driver_id : '',
          driver_match_name: auto ? sug.name : '',
          match_status: sug?.match_status || (g.driver_name ? 'unlinked' : 'unlinked'),
          match_type: auto ? sug.match_type : null,
          match_candidates: sug?.candidates || [],
          stops: canonStops(g),
        };
      });
      setPreview({ ...res.data, groups });
      toast.success(`Found ${groups.length} load${groups.length === 1 ? '' : 's'} in "${file.name}"`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (!open) { reset(); return; }
    if (initialFile) parseFile(initialFile);
  }, [open, initialFile, reset, parseFile]);

  const updateGroup = (idx, patch) => setPreview((p) => {
    if (!p) return p;
    const next = [...p.groups];
    next[idx] = { ...next[idx], ...patch };
    return { ...p, groups: next };
  });
  const applyDriverToSameName = (rawName, id, type, name) => setPreview((p) => {
    if (!p || !rawName) return p;
    return { ...p, groups: p.groups.map((g) => (g.driver_name === rawName ? { ...g, driver_id: id || '', match_type: type, driver_match_name: name || '' } : g)) };
  });
  const mutateStops = (gi, fn) => setPreview((p) => {
    if (!p) return p;
    const groups = [...p.groups];
    groups[gi] = { ...groups[gi], stops: fn([...(groups[gi].stops || [])]) };
    return { ...p, groups };
  });
  const updateStop = (gi, si, patch) => mutateStops(gi, (s) => { s[si] = { ...s[si], ...patch }; return s; });
  const addStop = (gi) => mutateStops(gi, (s) => [...s, { type: 'delivery', facility: '', address: '', city: '', state: '', zip: '', appointment_date: '', appointment_time: '' }]);
  const deleteStop = (gi, si) => mutateStops(gi, (s) => s.filter((_, i) => i !== si));
  const moveStop = (gi, si, dir) => mutateStops(gi, (s) => {
    const ni = si + dir;
    if (ni < 0 || ni >= s.length) return s;
    [s[si], s[ni]] = [s[ni], s[si]];
    return s;
  });

  const doImport = async () => {
    if (!preview) return;
    const problems = [];
    preview.groups.forEach((g) => {
      const stops = g.stops || [];
      if (!stops.length) { problems.push(`${g.trip_id || g.load_ref}: add at least one stop`); return; }
      stops.forEach((s, i) => { if (!stopHasLocation(s)) problems.push(`${g.trip_id || g.load_ref} · stop ${i + 1}: needs a facility or city`); });
      if (!g.load_ref) problems.push(`${g.trip_id}: missing Load ID`);
    });
    if (problems.length) {
      toast.error(`Please fix ${problems.length} item${problems.length === 1 ? '' : 's'} before importing`);
      setValidationIssues(problems);
      return;
    }
    setValidationIssues([]);
    setImporting(true);
    try {
      const res = await axios.post(`${API}/loads/amazon/import`, {
        groups: preview.groups.map((g) => {
          const stops = g.stops || [];
          const first = stops[0] || {};
          const last = stops[stops.length - 1] || {};
          return {
            trip_id: g.trip_id,
            load_ref: g.load_ref,
            load_rate: Number(g.load_rate || 0),
            total_miles: Number(g.total_miles || 0),
            status: g.status || 'assigned',
            broker: g.broker || 'Amazon Relay',
            driver_id: g.driver_id || null,
            driver_name_raw: g.driver_name || '',
            match_type: g.driver_id ? (g.match_type || 'manual') : null,
            pickup_date: first.appointment_date || g.pickup_date || '',
            delivery_date: g.delivery_date || last.appointment_date || '',
            notes: g.notes || '',
            pickup: {},
            delivery: {},
            stops,
            equipment_type: g.equipment_type || '',
            trailer_id: g.trailer_id || '',
            tractor_id: g.tractor_id || '',
            bucket: g.bucket || null,
            trip_stage_raw: g.trip_stage_raw || null,
            estimated_revenue: g.estimated_revenue || null,
            segments: g.segments || [],
            driver_conflict: !!g.driver_conflict,
          };
        }),
        overwrite_duplicates: overwrite,
        include_cancelled: includeCancelled,
        source_file: preview.source_file || null,
      }, { withCredentials: true });
      setImportResult(res.data);
      toast.success(
        `Imported ${res.data.counts.created}` +
        (res.data.counts.replaced ? ` (+ ${res.data.counts.replaced} replaced)` : '') +
        (res.data.counts.skipped ? ` · ${res.data.counts.skipped} skipped` : '')
      );
      onDone?.(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const totals = useMemo(() => {
    if (!preview) return null;
    const matched = preview.groups.filter((g) => g.driver_id).length;
    const needsConfirm = preview.groups.filter((g) => !g.driver_id && g.match_status === 'needs_confirmation').length;
    return {
      groups: preview.groups.length,
      rate: preview.groups.reduce((s, g) => s + Number(g.load_rate || 0), 0),
      miles: preview.groups.reduce((s, g) => s + Number(g.total_miles || 0), 0),
      matched,
      needsConfirm,
      unmatched: preview.groups.length - matched - needsConfirm,
      duplicates: preview.groups.filter((g) => g.duplicate_of).length,
    };
  }, [preview]);

  const activeDrivers = useMemo(() => (drivers || []).filter((d) => d.status === 'active' && d.user_id), [drivers]);

  const payRows = useMemo(() => (preview?.groups || []).map((g, i) => ({
    key: String(i), driver_id: g.driver_id || null, load_rate: Number(g.load_rate || 0), total_miles: Number(g.total_miles || 0), status: g.status || 'assigned',
  })), [preview]);
  const pay = usePayPreview(payRows, !!preview && !importResult);
  const noPayDrivers = useMemo(() => missingPayDrivers(pay.byKey), [pay.byKey]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="multi-load-import-modal">
      <div className="bg-white w-full max-w-7xl rounded-none sm:rounded-md shadow-2xl flex flex-col max-h-screen sm:max-h-[95vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Import Load List
            </h2>
            <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Amazon Relay trip list or any broker's load list — PDF, JPG, PNG screenshot, or Amazon Relay CSV. Multiple loads per file; mark completed trips as Delivered so they flow into payroll.
            </p>
          </div>
          <button data-testid="close-multi-import" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {!preview && (
            <div className="bg-white border-2 border-dashed border-slate-300 rounded-lg p-10 text-center" data-testid="multi-import-uploader">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Upload a load list
              </p>
              <p className="text-sm text-slate-500 mt-1">
                PDF, JPG, PNG (screenshots read by AI vision) or an Amazon Relay CSV export · up to 15 MB
              </p>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                onChange={(e) => parseFile(e.target.files?.[0])}
                disabled={busy}
                data-testid="multi-import-file-input"
                className="mt-5 mx-auto block text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#002FA7] file:text-white hover:file:bg-[#00247D] disabled:opacity-50"
              />
              {busy && (
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-600" data-testid="multi-import-busy">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Reading “{fileName}”… this can take up to a minute.
                </div>
              )}
            </div>
          )}

          {preview && totals && (
            <div className="grid grid-cols-2 md:grid-cols-8 gap-3 mb-5" data-testid="multi-import-summary">
              <Stat label="Loads found" value={totals.groups} />
              <Stat label="Total rate" value={fmtMoney(totals.rate)} />
              <Stat label="Total miles" value={fmtMiles(totals.miles)} />
              <Stat label="Driver matched" value={totals.matched} color="#16a34a" />
              <Stat label="Needs confirmation" value={totals.needsConfirm} color={totals.needsConfirm > 0 ? '#d97706' : '#16a34a'} />
              <Stat label="Unlinked" value={totals.unmatched} color={totals.unmatched > 0 ? '#64748b' : '#16a34a'} />
              <Stat label="Already in TMS" value={totals.duplicates} color={totals.duplicates > 0 ? '#d97706' : '#16a34a'} />
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="multi-import-payroll-impact">
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 flex items-center gap-1"><Wallet className="w-3 h-3" /> Payroll impact</p>
                <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  {pay.loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : fmtPay(pay.total)}
                </p>
                <p className="text-[10px] text-emerald-800">{pay.counted} delivered load{pay.counted === 1 ? '' : 's'} with a driver</p>
              </div>
            </div>
          )}

          {preview?.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-5 text-xs text-amber-900" data-testid="multi-import-warnings">
              <div className="font-bold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Warnings</div>
              <ul className="list-disc list-inside">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          {preview && !importResult && noPayDrivers.length > 0 && (
            <div className="mb-5"><PayStructureNudge drivers={noPayDrivers} /></div>
          )}

          {preview?.summary?.by_status && (
            <div className="mb-5 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid="amazon-reconciliation">
              {[['completed', 'Completed', 'border-emerald-200 bg-emerald-50', 'text-emerald-800'], ['cancelled', 'Cancelled', 'border-amber-200 bg-amber-50', 'text-amber-800'], ['rejected', 'Rejected', 'border-rose-200 bg-rose-50', 'text-rose-800'], ['review', 'Needs review', 'border-red-200 bg-red-50', 'text-red-800']].map(([k, label, box, txt]) => {
                const b = preview.summary.by_status[k]; if (!b) return null;
                return (
                  <div key={k} className={`rounded-md border p-2 ${box}`} data-testid={`amazon-bucket-${k}`}>
                    <p className={`font-bold uppercase tracking-wider ${txt}`}>{label}</p>
                    <p className="text-slate-900 font-semibold text-sm">{b.trips} trip{b.trips === 1 ? '' : 's'} · ${Number(b.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    {k === 'rejected' && <p className="text-[10px] text-rose-700">Imported as Rejected · $0 payable · driver accountability</p>}
                  </div>
                );
              })}
              <div className="rounded-md border border-slate-300 bg-white p-2" data-testid="amazon-bucket-total">
                <p className="font-bold uppercase tracking-wider text-slate-600">Source total</p>
                <p className="text-slate-900 font-semibold text-sm">{preview.summary.total_rows} rows · {preview.summary.total_groups} trips · ${Number(preview.summary.total_estimated_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                <p className="text-[10px] text-slate-500">Amazon estimated revenue — not settlement.{preview.summary.actions ? ` New ${preview.summary.actions.new} · Existing ${preview.summary.actions.existing} · Conflict ${preview.summary.actions.conflict}` : ''}</p>
              </div>
            </div>
          )}

          {preview && (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-5" data-testid="multi-import-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <Th>Trip / Load</Th>
                      <Th>Route</Th>
                      <Th right>Rate</Th>
                      <Th right>Miles</Th>
                      <Th>Driver (imported → registered)</Th>
                      <Th>Status</Th>
                      <Th right>Driver pay</Th>
                      <Th>Flags</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.groups.map((g, idx) => (
                      <React.Fragment key={`${g.trip_id}-${idx}`}>
                        <tr data-testid={`multi-import-row-${idx}`} className="border-t border-slate-100 align-top">
                          <td className="py-3 px-3">
                            <div className="font-bold text-[#002FA7]">{g.trip_id}</div>
                            <div className="text-[10px] text-slate-500">{g.broker}</div>
                            {g.bucket && g.bucket !== 'completed' && (
                              <span className={`inline-block mt-1 mr-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${g.bucket === 'cancelled' ? 'bg-amber-100 text-amber-800' : g.bucket === 'rejected' ? 'bg-rose-100 text-rose-800' : 'bg-red-100 text-red-700'}`} data-testid={`amazon-bucket-badge-${g.trip_id}`}>{g.bucket}</span>
                            )}
                            {g.action === 'conflict' && (
                              <span className="inline-block mt-1 mr-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase" data-testid={`amazon-conflict-${g.trip_id}`}>protected · {g.locked_reason}</span>
                            )}
                            {g.driver_conflict && (
                              <span className="inline-block mt-1 mr-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase">driver conflict</span>
                            )}
                            {g.leg_count > 1 && (
                              <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold uppercase">{g.segment_count || g.leg_count} segments · {g.stop_count || (g.stops || []).length} stops</span>
                            )}
                            {g.duplicate_of && (
                              <span className="inline-flex items-center gap-1 mt-1 ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase" data-testid={`multi-import-duplicate-${idx}`}>
                                <Copy className="w-3 h-3" /> exists
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-xs">
                            <div className="flex items-center gap-1 text-slate-900 font-semibold">
                              <MapPin className="w-3 h-3 text-slate-400" />
                              {g.pickup?.facility || g.pickup?.code || '—'}
                              {g.pickup?.city && <span className="font-normal text-slate-600">· {g.pickup.city}{g.pickup.state ? `, ${g.pickup.state}` : ''}</span>}
                            </div>
                            <div className="ml-4 text-slate-400"><ArrowRight className="w-3 h-3 inline-block" /></div>
                            <div className="flex items-center gap-1 text-slate-900 font-semibold">
                              <MapPin className="w-3 h-3 text-slate-400" />
                              {g.delivery?.facility || g.delivery?.code || '—'}
                              {g.delivery?.city && <span className="font-normal text-slate-600">· {g.delivery.city}{g.delivery.state ? `, ${g.delivery.state}` : ''}</span>}
                            </div>
                            {g.pickup_date && <div className="text-[10px] text-slate-400 mt-0.5">{g.pickup_date} → {g.delivery_date || '—'}</div>}
                            <button
                              type="button"
                              onClick={() => setExpanded(expanded === idx ? null : idx)}
                              data-testid={`multi-import-toggle-stops-${idx}`}
                              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#002FA7] hover:text-[#00247D]"
                            >
                              {expanded === idx ? '▾' : '▸'} Stops ({(g.stops || []).length})
                            </button>
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-semibold text-slate-900">
                            <input
                              type="number" step="0.01" value={g.load_rate ?? ''}
                              onChange={(e) => updateGroup(idx, { load_rate: e.target.value })}
                              data-testid={`multi-import-rate-${idx}`}
                              className="w-24 text-right border border-slate-300 rounded px-1.5 py-1 text-xs"
                            />
                            {g.rate_per_mile ? <div className="text-[10px] text-slate-400 font-normal">${g.rate_per_mile}/mi</div> : null}
                          </td>
                          <td className="py-3 px-3 text-right text-slate-700">
                            <input
                              type="number" step="1" value={g.total_miles ?? ''}
                              onChange={(e) => updateGroup(idx, { total_miles: e.target.value })}
                              data-testid={`multi-import-miles-${idx}`}
                              className="w-20 text-right border border-slate-300 rounded px-1.5 py-1 text-xs"
                            />
                          </td>
                          <td className="py-3 px-3">
                            <DriverMatchCell
                              testIdPrefix={`multi-import-driver-${idx}`}
                              originalName={g.driver_name}
                              matchStatus={g.match_status}
                              matchType={g.match_type}
                              driverId={g.driver_id || null}
                              driverName={g.driver_match_name}
                              candidates={g.match_candidates}
                              drivers={activeDrivers}
                              onChange={(id, type, name) => updateGroup(idx, { driver_id: id || '', match_type: type, driver_match_name: name || '' })}
                              sameNameCount={g.driver_name ? preview.groups.filter((o, j) => j !== idx && o.driver_name === g.driver_name && o.driver_id !== g.driver_id).length : 0}
                              onApplyToAll={(id, type, name) => applyDriverToSameName(g.driver_name, id, type, name)}
                            />
                          </td>
                          <td className="py-3 px-3 text-xs">
                            {['rejected', 'cancelled'].includes(g.status) ? (
                              <span className={`inline-block text-[10px] font-bold uppercase border rounded-md px-1.5 py-1 ${g.status === 'rejected' ? 'text-rose-800 border-rose-300 bg-rose-50' : 'text-amber-800 border-amber-300 bg-amber-50'}`} data-testid={`multi-import-status-fixed-${idx}`} title="Amazon status is preserved and cannot be changed during import">
                              {g.status.toUpperCase()}
                              </span>
                            ) : (
                            <select
                              value={g.status || 'assigned'}
                              onChange={(e) => updateGroup(idx, { status: e.target.value })}
                              data-testid={`multi-import-status-select-${idx}`}
                              className={`text-[10px] font-bold uppercase border rounded-md px-1.5 py-1 bg-white cursor-pointer ${
                                g.status === 'delivered' ? 'text-emerald-700 border-emerald-300'
                                  : g.status === 'in_transit' ? 'text-blue-700 border-blue-300' : 'text-slate-700 border-slate-300'
                              }`}
                              title="Set to Delivered so a completed load flows into payroll for its delivery date"
                            >
                              <option value="assigned">ASSIGNED</option>
                              <option value="in_transit">IN TRANSIT</option>
                              <option value="delivered">DELIVERED</option>
                            </select>
                            )}
                            <input
                              type="date"
                              value={toISODate(g.delivery_date || (g.stops?.[g.stops.length - 1]?.appointment_date) || '')}
                              onChange={(e) => updateGroup(idx, { delivery_date: e.target.value })}
                              data-testid={`multi-import-delivery-date-${idx}`}
                              title="Delivery date — sets the pay period this load lands in"
                              className="mt-1 block w-[130px] border border-slate-300 rounded px-1.5 py-1 text-[11px] text-slate-700"
                            />
                          </td>
                          <td className="py-3 px-3 text-right text-xs" data-testid={`multi-import-pay-${idx}`}>
                            {(() => {
                              const p = pay.byKey[String(idx)];
                              if (!p) return <span className="text-slate-400">—</span>;
                              const noPay = p.driver_record_id && p.has_pay_structure === false ? (
                                <a href={`/drivers/${p.driver_record_id}?setup=pay`} target="_blank" rel="noreferrer" data-testid={`multi-import-pay-setup-${idx}`}
                                   className="block text-[10px] font-semibold text-amber-700 hover:underline">No pay structure · set up →</a>
                              ) : null;
                              if (!p.counts_toward_payroll) return <div><span className="text-slate-400 italic" title={p.reason}>{p.reason === 'No driver assigned' ? 'no driver' : 'not delivered'}</span>{noPay}</div>;
                              return (
                                <div>
                                  <span className="font-mono font-semibold text-emerald-700">{fmtPay(p.estimated_pay)}</span>
                                  <div className="text-[10px] text-slate-500" title={p.note || ''}>{p.basis}</div>
                                  {noPay}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-3 text-[10px]">
                            {g.duplicate_of && <p className="text-amber-700 font-semibold mb-1">Load ref already exists ({g.duplicate_status || 'saved'}) — will be skipped unless overwrite is on.</p>}
                            {g.warnings?.length > 0 && (
                              <ul className="text-amber-700 list-disc list-inside">{g.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                            )}
                          </td>
                        </tr>
                        {expanded === idx && (
                          <tr data-testid={`multi-import-stops-editor-${idx}`} className="bg-slate-50/70">
                            <td colSpan={8} className="px-4 py-4">
                              <MultiLoadStopEditor stops={g.stops || []} gi={idx} onUpdate={updateStop} onAdd={addStop} onDelete={deleteStop} onMove={moveStop} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {validationIssues.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-xs text-red-800" data-testid="multi-import-validation">
              <div className="font-bold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Fix these before importing</div>
              <ul className="list-disc list-inside space-y-0.5">{validationIssues.map((v, i) => <li key={i}>{v}</li>)}</ul>
            </div>
          )}

          {importResult && (
            <div className="bg-white border border-emerald-200 rounded-md p-5" data-testid="multi-import-result">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Import complete</h3>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <Stat label="Created" value={importResult.counts.created} color="#16a34a" />
                <Stat label="Replaced" value={importResult.counts.replaced} color="#2563eb" />
                <Stat label="Skipped" value={importResult.counts.skipped} color={importResult.counts.skipped ? '#dc2626' : '#16a34a'} />
              </div>
              {importResult.skipped?.length > 0 && (
                <ul className="mt-3 text-xs text-slate-700 space-y-0.5">
                  {importResult.skipped.map((s, i) => <li key={i}>· <strong>{s.trip_id || s.load_ref || '?'}</strong>: {s.reason}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        {preview && (
          <div className="border-t border-slate-200 px-6 py-3 flex flex-wrap items-center justify-between gap-3 bg-white" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <label className="text-xs text-slate-700 flex items-center gap-2">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="multi-import-overwrite-checkbox" className="rounded border-slate-300" />
              Overwrite loads whose Load ID already exists
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} data-testid="amazon-include-cancelled-checkbox" className="rounded border-slate-300" />
              Also import cancelled trips (as Cancelled, for review — not payable)
            </label>
            <div className="flex items-center gap-2">
              <button onClick={() => { setPreview(null); setImportResult(null); }} className="px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900" data-testid="multi-import-discard-button">
                Upload another file
              </button>
              {importResult ? (
                <button onClick={onClose} data-testid="multi-import-done-button" className="px-5 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-md font-semibold text-sm">Done</button>
              ) : (
                <button
                  onClick={doImport}
                  disabled={importing || !preview.groups.length}
                  data-testid="multi-import-commit-button"
                  className="inline-flex items-center gap-2 px-5 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-md font-semibold text-sm disabled:opacity-50"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {(() => {
                    const n = preview.groups.filter((g) => !g.duplicate_of && g.action !== 'conflict' && g.bucket !== 'review' && (includeCancelled || g.bucket !== 'cancelled')).length;
                    return `Import ${n} new load${n === 1 ? '' : 's'}`;
                  })()}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color = '#002FA7' }) => (
  <div className="bg-white border border-slate-200 rounded-md p-3">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className="text-xl font-bold" style={{ color, fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
  </div>
);

const Th = ({ children, right }) => (
  <th className={`py-2 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

export default MultiLoadImportModal;
